import { useCallback, useRef, useState } from 'react';
import { Panel } from './Page';
import { EmptyState, ErrorState, TableSkeleton } from './Feedback';
import { Chip } from './Chips';
import { TableWrap } from './Table';
import { VendorIntakeReview } from './VendorIntakeReview';
import { documents } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { DocumentStatus, DocumentStatusLabels, GatewayScenario } from '../lib/enums';
import type { DocumentSummaryDto } from '../lib/types';

/**
 * TEACHING A SUPPLIER, ON THE SUPPLIER'S OWN CARD.
 *
 * This used to be a separate landing page: drop an invoice somewhere central, then go and find the
 * vendor it belonged to. That ordering is backwards for the job it serves. The operator already
 * knows who the supplier is - they are looking at them - and what they are doing is saying "this is
 * my vendor, and this is what his invoice looks like". Making them leave the card to say it loses
 * the one piece of context that was never in doubt.
 *
 * So the panel is bound to whatever card it sits on:
 *
 *   AN EXISTING VENDOR. The upload carries that vendor's QuickBooks id, so the intake can only ever
 *   update THAT supplier - it cannot create a second one from an invoice that happens to read
 *   slightly differently. The history below is the same binding read back, so a card shows what has
 *   already been taught about this supplier and what is still mid-review.
 *
 *   A VENDOR BEING CREATED. There is no id to bind to yet, so the document identifies the supplier
 *   and the review creates them - which is exactly what the standalone page does for an unknown
 *   supplier, reached from the card instead of from a nav entry.
 *
 * Nothing here is a second ingestion or posting path: it is the same uploader, the same evidence
 * retention and duplicate controls, and the same review component the Vendor Intake page uses.
 */
export function VendorTeachingPanel({
  qboVendorId,
  vendorName,
  onCompleted,
}: {
  /** The supplier this teaching is bound to; null while a vendor is being created. */
  qboVendorId: string | null;
  vendorName: string | null;
  /** Fired when an intake was approved, so the card can refresh or close. */
  onCompleted: () => void;
}): JSX.Element {
  const toast = useToast();
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Only a bound card has a history to show. An unsaved vendor has no id to filter on, and listing
  // every unbound intake here would put other people's in-progress work on this supplier's card.
  const history = useAsync(
    (signal) =>
      qboVendorId
        ? documents.list(
            { scenario: GatewayScenario.NewVendor, qboVendorId, page: 1, pageSize: 10 },
            signal,
          )
        : Promise.resolve(null),
    [qboVendorId],
  );

  const detail = useAsync(
    (signal) => (selectedId ? documents.get(selectedId, signal) : Promise.resolve(null)),
    [selectedId],
  );

  // WHETHER THIS SUPPLIER HAS ALREADY BEEN TAUGHT, read from the history rather than assumed. A
  // vendor created FROM an invoice already has a layout on file, so calling that "teach" would be
  // telling the operator something they can see is untrue - and the honest word changes what the
  // action means: the next invoice UPDATES a template rather than establishing one.
  const alreadyTaught = (history.data?.items ?? []).some(
    (row) => row.status === DocumentStatus.Posted,
  );

  const upload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      setUploading(true);
      try {
        // The SAME uploader every other document uses. Teaching a layout is a scenario, not a
        // second ingestion path, and the bound vendor id is what stops it creating a duplicate.
        const handle = documents.upload(files[0], {
          scenario: GatewayScenario.NewVendor,
          qboVendorId,
          targetQboEntityId: null,
          notes: qboVendorId
            ? `Invoice template for ${vendorName ?? qboVendorId}.`
            : 'Vendor intake for a new supplier, from the vendor card.',
        });

        const created = await handle.promise;
        toast.success('Analysing the invoice. The supplier details will appear here shortly.');
        setSelectedId(created.id);
        if (qboVendorId) await history.reload();
      } catch (error) {
        toast.error(errorMessage(error));
      } finally {
        setUploading(false);
      }
    },
    [qboVendorId, vendorName, history, toast],
  );

  // Reviewing happens in place. Opening another drawer on top of the card would put the supplier
  // being taught behind the thing teaching it.
  if (selectedId) {
    return (
      <div className="stack">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setSelectedId(null);
            if (qboVendorId) void history.reload();
          }}
        >
          &larr; Back to this supplier
        </button>

        {detail.loading ? (
          <TableSkeleton rows={6} />
        ) : detail.error ? (
          <ErrorState message={errorMessage(detail.error)} onRetry={detail.reload} />
        ) : detail.data ? (
          <VendorIntakeReview
            detail={detail.data}
            documentId={selectedId}
            boundVendorId={qboVendorId}
            onDone={async () => {
              setSelectedId(null);
              if (qboVendorId) await history.reload();
              onCompleted();
            }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="stack">
      {qboVendorId ? (
        alreadyTaught ? (
          <p className="muted">
            iERP AI already knows what this supplier's invoices look like. A newer invoice updates
            that template and can update the supplier's details - it does not create a bill.
          </p>
        ) : null
      ) : (
        <p className="muted">
          Dropping an invoice here identifies the supplier and creates them from the document - you
          do not have to fill in the details above as well.
        </p>
      )}

      <div
        className={['dropzone', dragging ? 'dropzone-active' : ''].filter(Boolean).join(' ')}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          void upload(event.dataTransfer.files);
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
      >
        <div className="dropzone-inner">
          <span className="dropzone-icon" aria-hidden="true">
            {uploading ? '…' : '⬇'}
          </span>
          <p className="dropzone-title">
            {uploading
              ? 'Uploading'
              : dragging
                ? 'Release to analyse'
                : qboVendorId
                  ? alreadyTaught
                    ? `Drag a newer invoice from ${vendorName ?? 'this supplier'} here`
                    : `Drag an invoice from ${vendorName ?? 'this supplier'} here`
                  : 'Drag the supplier invoice here'}
          </p>
          <p className="dropzone-sub">
            PDF, PNG, JPG or TIFF &middot; only the first page is read, because this
            {alreadyTaught ? ' updates the' : ' teaches the'} invoice template and the supplier's
            details rather than processing the invoice
          </p>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Browse files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="visually-hidden"
            disabled={uploading}
            accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,application/pdf,image/*"
            aria-label="Choose an invoice to teach this supplier's layout"
            onChange={(event) => {
              void upload(event.target.files);
              event.target.value = '';
            }}
          />
        </div>
      </div>

      {qboVendorId ? (
        <Panel title="Invoices taught for this supplier">
          {history.loading ? (
            <TableSkeleton rows={3} />
          ) : history.error ? (
            <ErrorState message={errorMessage(history.error)} onRetry={history.reload} />
          ) : history.data && history.data.items.length > 0 ? (
            <TableWrap>
              <thead>
                <tr>
                  <th scope="col">Document</th>
                  <th scope="col">Status</th>
                  <th scope="col">Uploaded</th>
                  <th scope="col" className="col-actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.data.items.map((row: DocumentSummaryDto) => (
                  <tr key={row.id}>
                    <td>{row.fileName}</td>
                    <td>
                      <Chip tone={row.status === DocumentStatus.Posted ? 'good' : 'neutral'}>
                        {row.status === DocumentStatus.Posted
                          ? 'Taught'
                          : DocumentStatusLabels[row.status]}
                      </Chip>
                    </td>
                    <td>{formatDateTime(row.createdUtc)}</td>
                    <td className="col-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setSelectedId(row.id)}
                      >
                        {row.status === DocumentStatus.Posted ? 'View' : 'Review'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          ) : (
            <EmptyState
              title="Nothing taught yet"
              description="Drop one of this supplier's invoices above so iERP AI learns their layout before their bills are processed."
            />
          )}
        </Panel>
      ) : null}
    </div>
  );
}
